import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AppConfiguration } from '@schemas/app.interface';
import { UserData } from '@schemas/user.interface';
import { AppService } from '@services/app/app.service';
import { AuthService } from '@services/auth.service';
import { SharedService } from '@services/common/shared.service';
import { ProductService } from '@services/product.service';
import { UserService } from '@services/user.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, map, switchMap, zip } from 'rxjs';

@Component({
  selector: 'tvb-admin-impersonate',
  templateUrl: './admin-impersonate.component.html',
  styleUrls: ['./admin-impersonate.component.scss'],
})
export class AdminImpersonateComponent implements OnInit {
  public submitted = false;
  public errorMessage: string | null | undefined;
  public title = 'Admin Impersonate';
  applyWishlistData = { status: false, productID: '', hit: '' };

  constructor(
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private userService: UserService,
    private appService: AppService,
    private sharedService: SharedService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public productService: ProductService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params?.uuid && params?.access_token) {
        this.impersonateStart(params?.uuid, params?.access_token);
      }
    });
  }

  public impersonateStart(userUuid: string, adminToken: string) {
    this.spinner.show();
    zip(this.userService.impersonateAdmin(userUuid, adminToken), this.productService.applyWishlistObservable)
      .pipe(
        map(([loginResponse, applyWishlistResponse]): void => {
          if (loginResponse) {
            this.afterLogoutCleaning();
            this.userService.setIsImpersonateSession(true);
            this.loginImpersonatedUser(loginResponse);

            if (applyWishlistResponse !== undefined && applyWishlistResponse !== null) {
              if (applyWishlistResponse.status == true && applyWishlistResponse.productID != null) {
                this.productService.addToWishList(
                  applyWishlistResponse.productID,
                  loginResponse.user.id,
                  applyWishlistResponse.hit
                );
                this.productService.applyWishlist(this.applyWishlistData);
                this.productService.setWishlistProductID(applyWishlistResponse.productID);
              }
            }

            this.updateAppConfig(loginResponse.user);
          }
        }),
        switchMap(() => this.productService.getWishlist()),
        finalize(() => {
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (getWishlistResponse): void => {
          this.productService.updateUserWishlistRecords(getWishlistResponse);
          this.router.navigateByUrl('/');
        },
        error: (error): void => {
          this.router.navigateByUrl('/admin-impersonate');
          this.errorMessage = error?.message || error;
        },
      });
  }

  private loginImpersonatedUser(loginResponse: any) {
    this.afterLogoutCleaning();
    loginResponse!.user.access_token = loginResponse.access_token;
    loginResponse!.user.refresh_token = loginResponse.refresh_token;
    this.authService.setCart(loginResponse!.cart);
    this.userService.setUser(loginResponse!.user);
    this.authService.setLoggedIn(true);
  }

  private updateAppConfig(user: UserData) {
    const appConfig = {
      currencyID: user.currency_id || 11,
      currencyCode: this.appService.currencies.find(x => x.id === user.currency_id)?.name || 'EUR',
      currencySymbol: this.appService.currencies.find(x => x.id === user.currency_id)?.symbol || '€',
      languageID: user.language_id | 1,
      languageShortName: this.appService.languages.find(x => x.id === user.language_id)?.shortName || 'en',
      languageName: this.appService.languages.find(x => x.id === user.language_id)?.name || 'English(US)',
      countryID: user.country_id || 1,
      countryName: this.appService.countries.find(x => x.id === user.country_id)?.name || 'United States',
    } as AppConfiguration;

    this.appService.setAppConfiguration(appConfig);
  }

  public afterLogoutCleaning() {
    this.userService.setUser(null);
    this.appService.disableSettingsPopup(false);
    this.authService.afterLogout();
    this.sharedService.updateCartCount(0);
    this.sharedService.updateItemCount(0);
  }

  public getAdminLink() {
    return environment.HOST_URL + '/admin/login';
  }
}
